import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

// ─── Notification categories config ──────────────────────────────────────────
const CATEGORIES = [
  {
    key: 'order_updates',
    label: 'Order Updates',
    desc: 'Driver assigned, pickup, dropoff status',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4z" />
      </svg>
    ),
    color: 'text-blue-500',
    bg: 'bg-blue-500/10',
    channelKey: 'order_updates_channels',
  },
  {
    key: 'arrival_alerts',
    label: 'Arrival Alerts',
    desc: 'Driver arrived, geofence notifications',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
      </svg>
    ),
    color: 'text-green-500',
    bg: 'bg-green-500/10',
    channelKey: 'arrival_alerts_channels',
  },
  {
    key: 'earnings_payouts',
    label: 'Earnings & Payouts',
    desc: 'Payout processed, wallet updates',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" />
      </svg>
    ),
    color: 'text-yellow-500',
    bg: 'bg-yellow-500/10',
    channelKey: 'earnings_payouts_channels',
  },
  {
    key: 'promotions',
    label: 'Promotions',
    desc: 'Promo codes, discounts, special offers',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z" />
      </svg>
    ),
    color: 'text-purple-500',
    bg: 'bg-purple-500/10',
    channelKey: 'promotions_channels',
  },
  {
    key: 'system_messages',
    label: 'System Messages',
    desc: 'App updates, maintenance, announcements',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 9h-2V5h2v6zm0 4h-2v-2h2v2z" />
      </svg>
    ),
    color: 'text-slate-500',
    bg: 'bg-slate-500/10',
    channelKey: 'system_messages_channels',
  },
];

const CHANNELS = [
  { key: 'push', label: 'Push', icon: '🔔' },
  { key: 'sms', label: 'SMS', icon: '💬' },
  { key: 'email', label: 'Email', icon: '📧' },
];

const DEFAULT_PREFS = {
  order_updates: true,
  arrival_alerts: true,
  earnings_payouts: true,
  promotions: false,
  system_messages: true,
  order_updates_channels: { push: true, sms: true, email: false },
  arrival_alerts_channels: { push: true, sms: true, email: false },
  earnings_payouts_channels: { push: true, sms: false, email: true },
  promotions_channels: { push: false, sms: false, email: true },
  system_messages_channels: { push: true, sms: false, email: false },
};

// ─── Toggle component ─────────────────────────────────────────────────────────
const Toggle = ({ value, onChange, size = 'md' }) => {
  const sizes = {
    sm: { track: 'w-9 h-5', thumb: 'w-4 h-4', translate: 'translate-x-4' },
    md: { track: 'w-11 h-6', thumb: 'w-5 h-5', translate: 'translate-x-5' },
  };
  const s = sizes?.[size] || sizes?.md;
  return (
    <button
      onClick={onChange}
      className={`relative ${s?.track} rounded-full transition-colors flex-shrink-0 ${value ? 'bg-[#1a365d]' : 'bg-slate-200'}`}
    >
      <span className={`absolute top-0.5 left-0.5 ${s?.thumb} bg-white rounded-full shadow transition-transform ${value ? s?.translate : 'translate-x-0'}`} />
    </button>
  );
};

// ─── Main NotificationPreferencesDrawer ──────────────────────────────────────
const NotificationPreferencesDrawer = ({ isOpen, onClose }) => {
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const drawerRef = useRef(null);

  // ─── Load preferences ───────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const loadPrefs = async () => {
      setLoading(true);
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (!user) { setLoading(false); return; }

        const { data, error } = await supabase
          ?.from('notification_preferences')
          ?.select('*')
          ?.eq('user_id', user?.id)
          ?.maybeSingle();

        if (!error && data) {
          setPrefs({
            order_updates: data?.order_updates ?? true,
            arrival_alerts: data?.arrival_alerts ?? true,
            earnings_payouts: data?.earnings_payouts ?? true,
            promotions: data?.promotions ?? false,
            system_messages: data?.system_messages ?? true,
            order_updates_channels: data?.order_updates_channels || DEFAULT_PREFS?.order_updates_channels,
            arrival_alerts_channels: data?.arrival_alerts_channels || DEFAULT_PREFS?.arrival_alerts_channels,
            earnings_payouts_channels: data?.earnings_payouts_channels || DEFAULT_PREFS?.earnings_payouts_channels,
            promotions_channels: data?.promotions_channels || DEFAULT_PREFS?.promotions_channels,
            system_messages_channels: data?.system_messages_channels || DEFAULT_PREFS?.system_messages_channels,
          });
        }
      } catch (err) {
        console.error('Failed to load notification preferences:', err);
      } finally {
        setLoading(false);
      }
    };
    loadPrefs();
  }, [isOpen]);

  // ─── Close on outside click ─────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (drawerRef?.current && !drawerRef?.current?.contains(e?.target)) {
        onClose?.();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const handleCategoryToggle = (key) => {
    setPrefs(prev => ({ ...prev, [key]: !prev?.[key] }));
    setSaved(false);
  };

  const handleChannelToggle = (channelKey, channel) => {
    setPrefs(prev => ({
      ...prev,
      [channelKey]: {
        ...prev?.[channelKey],
        [channel]: !prev?.[channelKey]?.[channel],
      },
    }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;

      const upsertData = {
        user_id: user?.id,
        order_updates: prefs?.order_updates,
        arrival_alerts: prefs?.arrival_alerts,
        earnings_payouts: prefs?.earnings_payouts,
        promotions: prefs?.promotions,
        system_messages: prefs?.system_messages,
        order_updates_channels: prefs?.order_updates_channels,
        arrival_alerts_channels: prefs?.arrival_alerts_channels,
        earnings_payouts_channels: prefs?.earnings_payouts_channels,
        promotions_channels: prefs?.promotions_channels,
        system_messages_channels: prefs?.system_messages_channels,
      };

      const { error } = await supabase
        ?.from('notification_preferences')
        ?.upsert(upsertData, { onConflict: 'user_id' });

      if (error) throw error;
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      console.error('Failed to save notification preferences:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[1600]" onClick={onClose} />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className="fixed right-0 top-0 bottom-0 w-full max-w-sm bg-white shadow-2xl z-[1700] flex flex-col"
        style={{ animation: 'slideInRight 0.3s ease' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-[#1a365d] flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="white" className="w-4 h-4">
                <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
              </svg>
            </div>
            <div>
              <h2 className="font-bold text-[#1a365d] text-sm">Notification Preferences</h2>
              <p className="text-slate-400 text-xs">Manage alerts & channels</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-slate-500">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-5 space-y-4">
              {[1, 2, 3, 4, 5]?.map(i => (
                <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="p-5 space-y-3">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">
                Notification Categories
              </p>
              {CATEGORIES?.map((cat) => (
                <div key={cat?.key} className="border border-slate-100 rounded-2xl overflow-hidden">
                  {/* Category row */}
                  <div className="flex items-center gap-3 p-3.5">
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${cat?.bg} ${cat?.color}`}>
                      {cat?.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800">{cat?.label}</p>
                      <p className="text-xs text-slate-400 truncate">{cat?.desc}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Expand channels */}
                      <button
                        onClick={() => setExpandedCategory(expandedCategory === cat?.key ? null : cat?.key)}
                        className="w-7 h-7 rounded-lg bg-slate-50 hover:bg-slate-100 flex items-center justify-center transition-colors"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className={`w-3.5 h-3.5 text-slate-400 transition-transform ${expandedCategory === cat?.key ? 'rotate-180' : ''}`}
                        >
                          <path d="M18 15l-6-6-6 6" />
                        </svg>
                      </button>
                      <Toggle value={prefs?.[cat?.key]} onChange={() => handleCategoryToggle(cat?.key)} />
                    </div>
                  </div>

                  {/* Channel sub-toggles */}
                  {expandedCategory === cat?.key && prefs?.[cat?.key] && (
                    <div className="border-t border-slate-50 bg-slate-50/50 px-3.5 py-3 space-y-2.5">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Delivery Channels</p>
                      {CHANNELS?.map(({ key: ch, label, icon }) => (
                        <div key={ch} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{icon}</span>
                            <span className="text-sm text-slate-600">{label}</span>
                          </div>
                          <Toggle
                            value={prefs?.[cat?.channelKey]?.[ch] ?? false}
                            onChange={() => handleChannelToggle(cat?.channelKey, ch)}
                            size="sm"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-slate-100 flex-shrink-0">
          <button
            onClick={handleSave}
            disabled={saving}
            className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 ${
              saved ? 'bg-green-500 text-white' : 'bg-[#1a365d] text-white hover:bg-[#2d5a87]'
            }`}
          >
            {saving ? (
              <>
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="30 70" />
                </svg>
                Saving...
              </>
            ) : saved ? (
              <>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                Preferences Saved!
              </>
            ) : (
              'Save Preferences'
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
};

export default NotificationPreferencesDrawer;
